import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import TabsSection from 'components/tabs/tabs-section/TabsSection';
import Spinner from 'components/spinner/Spinner';

import SOVQueriesInputCheckbox from 'modules/insights/sov/components/queries/inputs/checkbox/SOVQueriesInputCheckbox';
import SOVQueriesInputCustom from 'modules/insights/sov/components/queries/inputs/field/custom/SOVQueriesInputCustom';

import {
  addSearchResultCustom,
  fetchSearchResults,
  removeSearchResultCustom,
  updateSearchResults
} from 'modules/insights/sov/reducers/queries/consumer-insights/search-results/sov-queries-search-results';

import { __t, applyTranslations } from 'utils/language';
import { SINGLE, TAB_LABELS, getSingular } from 'modules/insights/sov/utils/queries/sov-queries';
import { getCustomValueCheckbox } from 'modules/insights/sov/utils/queries/search-results/sov-queries-search-results';

import './sov-queries-modal-step-search-results.scss';

import type {
  CheckboxesListItem,
  TabsSectionItem,
  TextEntryKeyboardEvent
} from 'types';
import type {
  SOVGeneralModalTextFields,
  SOVQueryLocal,
  SOVQuerySearchResultsLocal,
  SOVQuerySearchResultsType,
  SOVQueryStep,
  SOVQueryStepType
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

type OwnProps = {
  reportId: number,
  query: SOVQueryLocal,
  isMulti: boolean,
  activeTab: string,
  stepDirection: number,
  stepInfo: SOVQueryStep,
  textFields: SOVGeneralModalTextFields,
  onTabsSwitch: (a?: string | null) => void,
  onSetActionButtonDisabledStatus: (a: boolean) => void
};

type StateProps = {
  results: SOVQuerySearchResultsLocal,
  resultsCustom: SOVQuerySearchResultsLocal,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchResults: (d: number, c: string, b: number | undefined | null, a: SOVQueryStepType) => void,
  dispatchUpdateResults: (
    e: CheckboxesListItem,
    d: SOVQuerySearchResultsType,
    c: string,
    b: SOVQueryStepType,
    a?: boolean | null
  ) => void,
  dispatchAddResultCustom: (c: CheckboxesListItem, b: string, a: SOVQueryStepType) => void,
  dispatchRemoveResultCustom: (c: CheckboxesListItem, b: string, a: SOVQueryStepType) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  customValue: string
};

const SHARED_MSG_KEY = 'sov_shared';
const MODAL_MSG_KEY = 'sov_queries_modal';

class SOVQueriesModalStepSearchResults extends React.PureComponent<Props, State> {
  state = {
    customValue: ''
  };

  componentDidMount() {
    const { reportId, query, stepDirection, textFields, dispatchFetchResults } = this.props;
    const stepType = this.getStepType();
    const searchTerm = textFields[String(stepType)] || null;

    this.getIsActionButtonDisabledStatus();

    if (searchTerm && stepDirection !== -1) {
      dispatchFetchResults(reportId, searchTerm, query.id, stepType);
    }
  }

  componentDidUpdate() {
    this.getIsActionButtonDisabledStatus();
  }

  componentWillUnmount() {
    const { onSetActionButtonDisabledStatus } = this.props;

    onSetActionButtonDisabledStatus(true);
  }

  getStepType(): SOVQueryStepType {
    const { isMulti, stepInfo } = this.props;

    return !isMulti ? SINGLE : stepInfo.stepType;
  }

  getIsAllCheckedStatus(): boolean {
    const { results, resultsCustom, activeTab } = this.props;
    const stepType = this.getStepType();
    const activeResultsStandard = results[stepType][activeTab] || [];
    const activeResultsCustom = resultsCustom[stepType][activeTab] || [];
    const isUnchecked = activeResultsStandard.concat(activeResultsCustom).some(item => !item.isSelected);

    return !isUnchecked;
  }

  getIsActionButtonDisabledStatus(): void {
    const { results, resultsCustom, onSetActionButtonDisabledStatus } = this.props;
    const stepType = this.getStepType();
    const activeResultsStandard = results[stepType] || {};
    const activeResultsCustom = resultsCustom[stepType] || {};

    const isOneKeywordSelected = TAB_LABELS.some(key => {
      const allKeywords = activeResultsStandard[key].concat(activeResultsCustom[key]);
      return allKeywords.some(item => item.isSelected);
    });

    onSetActionButtonDisabledStatus(!isOneKeywordSelected);
  }

  getTabDetails = (type: string): TabsSectionItem => ({
    label: __t(`${SHARED_MSG_KEY}.labels.${type}`),
    identifier: type,

    tooltip: {
      message: __t(`${SHARED_MSG_KEY}.tooltips.${type}`),
      width: 'long'
    },

    className: 'sov-queries-modal-step-search-results__tab'
  });

  handleToggleAll = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { activeTab, dispatchUpdateResults } = this.props;
    const stepType = this.getStepType();

    dispatchUpdateResults(item, 'results', activeTab, stepType, !item.isSelected);
    dispatchUpdateResults(item, 'resultsCustom', activeTab, stepType, !item.isSelected);
  };

  handleToggleItem = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { activeTab, dispatchUpdateResults } = this.props;
    const stepType = this.getStepType();

    dispatchUpdateResults(item, 'results', activeTab, stepType);
  };

  handleToggleCustom = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { activeTab, dispatchUpdateResults } = this.props;
    const stepType = this.getStepType();

    dispatchUpdateResults(item, 'resultsCustom', activeTab, stepType);
  };

  handleCustomAdd = (): void => {
    const { activeTab, dispatchAddResultCustom } = this.props;
    const { customValue } = this.state;
    const item = getCustomValueCheckbox(customValue, getSingular(activeTab));
    const stepType = this.getStepType();

    this.setState({
      customValue: ''
    });

    dispatchAddResultCustom(item, activeTab, stepType);
  };

  handleCustomRemove = (event: SyntheticEvent<HTMLElement>, item: CheckboxesListItem): void => {
    const { activeTab, dispatchRemoveResultCustom } = this.props;
    const stepType = this.getStepType();

    this.setState({
      customValue: ''
    });

    dispatchRemoveResultCustom(item, activeTab, stepType);
  };

  handleCustomChange = (value: string): void => {
    this.setState({
      customValue: value
    });
  };

  handleCustomKeypress = (event: TextEntryKeyboardEvent): void => {
    const { customValue } = this.state;

    if (event.key === 'Enter' && !isEmpty(customValue.trim())) {
      event.preventDefault();
      this.handleCustomAdd();
    }
  };

  renderNotFound(): React.ReactNode {
    const { activeTab } = this.props;

    return (
      <div className="sov-queries-modal-step-search-results__not-found">
        {__t(`${MODAL_MSG_KEY}.messages.no_results_found`, {
          resultType: activeTab
        })}
        {__t(`${MODAL_MSG_KEY}.messages.min_followers`)}
      </div>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-search-results__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderInputCustom(): React.ReactNode {
    const { customValue } = this.state;

    return (
      <div className="sov-queries-modal-step-search-results__input-custom">
        <SOVQueriesInputCustom
          value={customValue}
          onChange={this.handleCustomChange}
          onAdd={this.handleCustomAdd}
          onKeyPress={this.handleCustomKeypress}
        />
      </div>
    );
  }

  renderSelectAll(): React.ReactNode {
    const id = 'sov-queries-modal-step-search-results__select-all';
    const allChecked = this.getIsAllCheckedStatus();

    return (
      <div className={id}>
        <SOVQueriesInputCheckbox
          items={[{
            id,
            label: __t(`${MODAL_MSG_KEY}.labels.select_all`),
            isSelected: allChecked
          }]}
          highlightedId={id}
          onCheckboxChange={this.handleToggleAll}
        />
      </div>
    );
  }

  renderCheckboxesCustom(): React.ReactNode {
    const { resultsCustom, activeTab } = this.props;
    const stepType = this.getStepType();
    const activeResults = resultsCustom[stepType][activeTab] || [];

    return !isEmpty(activeResults) && (
      <div className="sov-queries-modal-step-search-results__checkboxes-custom">
        <SOVQueriesInputCheckbox
          items={activeResults}
          onCheckboxChange={this.handleToggleCustom}
          onCheckboxRemove={this.handleCustomRemove}
        />
      </div>
    );
  }

  renderCheckboxesStandard(): React.ReactNode {
    const { results, activeTab } = this.props;
    const stepType = this.getStepType();
    const activeResults = results[stepType][activeTab] || [];

    return (
      <div className="sov-queries-modal-step-search-results__checkboxes-standard">
        <SOVQueriesInputCheckbox
          items={activeResults}
          onCheckboxChange={this.handleToggleItem}
        />
      </div>
    );
  }

  renderCheckboxes(): React.ReactNode {
    const { results, activeTab, isLoading } = this.props;

    if (isLoading) {
      return this.renderSpinner();
    }

    const stepType = this.getStepType();
    const activeResults = results[stepType][activeTab] || [];

    if (isEmpty(activeResults)) {
      return this.renderNotFound();
    }

    return (
      <React.Fragment>
        {this.renderCheckboxesCustom()}
        {this.renderSelectAll()}
        {this.renderCheckboxesStandard()}
      </React.Fragment>
    );
  }

  renderTabs(): React.ReactNode {
    const { activeTab, onTabsSwitch } = this.props;
    const tabs = TAB_LABELS.map(this.getTabDetails);

    return (
      <div className="sov-queries-modal-step-search-results__tabs">
        <TabsSection
          items={tabs}
          activeTab={activeTab}
          size="small"
          className="sov-queries-modal-step-search-results__tabs-section"
          onClick={onTabsSwitch}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="sov-queries-modal-step-search-results">
        {this.renderInputCustom()}
        <div className="sov-queries-modal-step-search-results__results">
          {this.renderTabs()}
          <div className="sov-queries-modal-step-search-results__checkboxes">
            {this.renderCheckboxes()}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ sovQueriesSearchResultsConsumerInsights }): StateProps => ({
  results: sovQueriesSearchResultsConsumerInsights.results,
  resultsCustom: sovQueriesSearchResultsConsumerInsights.resultsCustom,
  isLoading: sovQueriesSearchResultsConsumerInsights.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchResults: (reportId: number, searchTerm: string, queryId: number | undefined | null, stepType: SOVQueryStepType) => (
    dispatch(fetchSearchResults(reportId, searchTerm, queryId, stepType))
  ),

  dispatchUpdateResults: (
    item: CheckboxesListItem,
    resultsType: SOVQuerySearchResultsType,
    activeTab: string,
    stepType: SOVQueryStepType,
    statusOverride?: boolean | null
  ) => (
    dispatch(updateSearchResults(item, resultsType, activeTab, stepType, statusOverride))
  ),

  dispatchAddResultCustom: (item: CheckboxesListItem, activeTab: string, stepType: SOVQueryStepType) => (
    dispatch(addSearchResultCustom(item, activeTab, stepType))
  ),

  dispatchRemoveResultCustom: (item: CheckboxesListItem, activeTab: string, stepType: SOVQueryStepType) => (
    dispatch(removeSearchResultCustom(item, activeTab, stepType))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVQueriesModalStepSearchResults);
